import React, { useState, useEffect } from 'react';
import {TrendingUp, Calendar, IndianRupeeIcon} from 'lucide-react';
import api from '../../../api';
import './AccountSections.css';

const SalesSection = ({ userId }) => {
  const [salesData, setSalesData] = useState({
    totalRevenue: 0,
    thisMonth: 0,
    lastMonth: 0,
    growth: 0
  });

  const [recentSales, setRecentSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSalesData();
  }, [userId]);

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch sales data
      const dataResponse = await api.get(`/brands/${userId}/sales/data`);

      setSalesData(dataResponse.data || {
        totalRevenue: 0,
        thisMonth: 0,
        lastMonth: 0,
        growth: 0
      });

      // Fetch recent transactions
      const transactionsResponse = await api.get(`/brands/${userId}/sales/transactions?limit=10`);
      setRecentSales(transactionsResponse.data || []);
    } catch (err) {

      let errorMessage = 'Failed to load sales data';
      if (err.response) {
        if (err.response.status === 403) {
          errorMessage = 'Access denied. Please log in again.';
        } else if (err.response.status === 404) {
          errorMessage = err.response.data?.message || 'Brand not found';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }
      }

      setError(errorMessage);
      setSalesData({
        totalRevenue: 0,
        thisMonth: 0,
        lastMonth: 0,
        growth: 0
      });
      setRecentSales([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h2 className="section-title">Sales Dashboard</h2>
        <p className="section-description">Track your sales performance and revenue</p>
      </div>

      {loading && (
        <div className="loading-state">
          <p>Loading sales data...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="stats-grid">
            <div className="stat-card-simple">
              <div className="stat-icon-wrapper bg-blue-100">
                <IndianRupeeIcon size={24} className="text-blue-600" />
              </div>
              <div>
                <p className="stat-label">Total Revenue</p>
                <p className="stat-value-large">₹{(salesData.totalRevenue || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>

            <div className="stat-card-simple">
              <div className="stat-icon-wrapper bg-green-100">
                <Calendar size={24} className="text-green-600" />
              </div>
              <div>
                <p className="stat-label">This Month</p>
                <p className="stat-value-large">₹{(salesData.thisMonth || 0).toLocaleString('en-IN')}</p>
              </div>
            </div>

            <div className="stat-card-simple">
              <div className="stat-icon-wrapper bg-purple-100">
                <TrendingUp size={24} className="text-purple-600" />
              </div>
              <div>
                <p className="stat-label">Growth</p>
                <p className="stat-value-large text-green-600">+{(salesData.growth || 0).toFixed(1)}%</p>
              </div>
            </div>
          </div>

          <div className="table-container">
            <h3 className="table-title">Recent Sales</h3>
            {recentSales.length > 0 ? (
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentSales.map((sale) => (
                      <tr key={sale.id}>
                        <td className="font-medium">{sale.campaign}</td>
                        <td>{sale.date}</td>
                        <td className="text-green-600 font-semibold">₹{(sale.amount || 0).toLocaleString('en-IN')}</td>
                        <td>
                          <span className="status-badge completed">{sale.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 p-4">No sales transactions found</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SalesSection;

